import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEscapeClose from './useEscapeClose';

/**
 * Searchable picker over a list of plain strings, with an explicit "add this one" row — the same
 * interaction as the tracker's Partner / Line field, which Max asked these fields to match
 * (2026-08-13).
 *
 * Why not a free-text box: typing let "HEVN HR" / "hevn hr" / "HEVN  HR" become three teams that are
 * really one. Why not a closed dropdown either: new teams do appear, and forcing an admin round-trip
 * for that would stall the person filling the form. So: search what exists, and adding is a
 * deliberate second click that first folds anything differing only by case or spacing onto the
 * spelling already in use.
 *
 * The value IS the string — there are no ids here, so the picker never needs the server.
 */
/* `allowAdd` is false where the list is a CLOSED set — a ticket id either exists or it does not,
   and offering to add one would let a lead point at a ticket nobody ever raised (Max, 2026-09-22).
   It defaults to true, so every field already using this picker keeps the behaviour it had.
   `onAdd` is for a list the SERVER keeps (the courier Department, 2026-10-19): the add row hands the new
   name over instead of only setting it, so it joins the list for the next person. `addLabel` /
   `emptyLabel` let a translated screen word the two rows; left out, they read as they always have. */
export default function ListPicker({ value, options = [], onChange, placeholder, disabled, allowAdd = true,
  onAdd, addLabel, emptyLabel }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Separates "the box shows the current choice" from "the user is searching", so clicking the field
  // shows the whole list instead of filtering it down to the one already chosen.
  const [typed, setTyped] = useState(false);
  // Positioned FIXED, not absolute: the modal body scrolls and clips, which cut the bottom off the
  // list so the last entries could not be reached.
  const [menuBox, setMenuBox] = useState(null);
  const wrapRef = useRef(null);

  // Esc closes the menu only — the form behind it stays open (see useEscapeClose).
  useEscapeClose(() => setOpen(false), open);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const dropUp = below < 170 && above > below;
      setMenuBox({
        left: r.left,
        width: r.width,
        maxHeight: Math.min(260, Math.max(140, dropUp ? above : below)),
        ...(dropUp ? { bottom: window.innerHeight - r.top + 3 } : { top: r.bottom + 3 }),
      });
    };
    place();
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const norm = (v) => String(v || '').trim().replace(/\s+/g, ' ');
  const raw = norm(query);
  const q = raw.toLowerCase();
  const searching = typed && !!q;
  const matches = searching ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  // A name that differs only by case or spacing is the SAME name — offering to add it would create
  // the duplicate this picker exists to prevent.
  const sameAsExisting = options.find((o) => o.toLowerCase() === q);

  const pick = (v) => {
    onChange(v || '');
    setQuery('');
    setTyped(false);
    setOpen(false);
  };

  if (disabled) return <input type="text" value={value || ''} disabled />;

  return (
    <div className="pt-partner-picker" ref={wrapRef}>
      <div className="pt-partner-row">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={open ? query : (value || '')}
          placeholder={placeholder ?? t('xShell.searchPh')}
          onFocus={(e) => { setOpen(true); setTyped(false); setQuery(value || ''); e.target.select(); }}
          onChange={(e) => { setQuery(e.target.value); setTyped(true); setOpen(true); }}
        />
        {/* A CARET, so the field reads as a dropdown — Max, 2026-09-23: "thấy biểu tượng X, vẫn
            cần v ngược để hiểu nó như dropdown á". It showed only the × once something was chosen,
            which says "you can clear this" and says nothing at all about there being a list behind
            it; an empty one showed nothing but a placeholder and read as a plain text box.
            It is `aria-hidden` and not a button: the INPUT already opens the menu on focus and on
            click, so a second focusable control on top of it would be one more tab stop that does
            what the box under it does. */}
        {!!value && !open && (
          <button type="button" className="pt-partner-clear" onClick={() => pick('')} aria-label={t('xShell.clear')}>&times;</button>
        )}
        <svg className="pt-partner-caret" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false">
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {open && menuBox && (
        <div className="pt-partner-menu" style={menuBox}>
          {matches.map((o) => (
            <button
              key={o}
              type="button"
              className={`pt-partner-opt${o === value ? ' on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              {o}
            </button>
          ))}
          {!matches.length && <div className="pt-partner-empty">{emptyLabel ? emptyLabel(raw) : t('xShell.noMatchFor', { q: raw })}</div>}
          {allowAdd && searching && !sameAsExisting && (
            <button type="button" className="pt-partner-add" onMouseDown={(e) => e.preventDefault()}
              onClick={() => { if (onAdd) { onAdd(raw); setQuery(''); setTyped(false); setOpen(false); } else pick(raw); }}>
              {addLabel ? addLabel(raw) : t('xShell.addToList', { q: raw })}
            </button>
          )}
          {searching && sameAsExisting && sameAsExisting !== raw && (
            <button type="button" className="pt-partner-add" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(sameAsExisting)}>
              {t('xShell.alreadyExists', { name: sameAsExisting })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
